/** Cliente HTTP mínimo sobre fetch. Mismo origen: la cookie de sesión viaja sola. */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  /** Un `Blob` (p. ej. un archivo subido) va tal cual, con su propio tipo; el resto, JSON. */
  body?: unknown
  signal?: AbortSignal
}

/** Cookie `bt_csrf` que puso el login — a propósito no es `httponly`, para
 * que esto pueda leerla (ver app/security/csrf.py). Se reenvía en cada
 * mutación como X-CSRF-Token: sin esto, toda escritura rebota con 403 aunque
 * la sesión sea válida — el doble envío es la prueba de que el pedido salió
 * de esta misma página y no de un sitio ajeno. */
function readCsrfCookie(): string | undefined {
  const match = document.cookie.match(/(?:^|; )bt_csrf=([^;]+)/)
  const value = match?.[1]
  return value ? decodeURIComponent(value) : undefined
}

async function extractErrorMessage(response: Response): Promise<string> {
  try {
    const data = (await response.json()) as { detail?: unknown }
    if (typeof data.detail === 'string') return data.detail
    if (Array.isArray(data.detail)) return 'Datos inválidos'
  } catch {
    // Respuesta sin cuerpo JSON: caemos al mensaje genérico.
  }
  return `Error ${response.status}`
}

/** Query string a partir de un objeto, salteando lo que no está definido.
 *
 * Los `undefined` se omiten en vez de mandarse vacíos: un `?symbol=` es un
 * filtro por la cadena vacía para el backend, no la ausencia de filtro. */
export function buildQuery(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) search.set(key, String(value))
  }
  const query = search.toString()
  return query ? `?${query}` : ''
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal } = options

  const headers: Record<string, string> = {}
  if (body instanceof Blob) headers['Content-Type'] = body.type
  else if (body !== undefined) headers['Content-Type'] = 'application/json'
  // GET/HEAD no lo necesitan (el middleware sólo lo exige en mutaciones), y
  // antes del primer login todavía no existe la cookie que leer.
  if (method !== 'GET') {
    const csrfToken = readCsrfCookie()
    if (csrfToken) headers['X-CSRF-Token'] = csrfToken
  }

  const response = await fetch(`/api${path}`, {
    method,
    signal,
    credentials: 'same-origin',
    headers: Object.keys(headers).length ? headers : undefined,
    body: body === undefined ? undefined : body instanceof Blob ? body : JSON.stringify(body),
  })

  if (!response.ok) throw new ApiError(response.status, await extractErrorMessage(response))
  if (response.status === 204) return undefined as T

  return (await response.json()) as T
}
